import React from 'react'
import {
  FileText,
  Upload,
  FileSignature,
  Sparkles,
  Eye,
  CheckCircle,
  Circle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { HubDocument } from '../../config/hubDocuments'

export interface DocumentStatus {
  uploaded: boolean
  signed: boolean
}

interface DocumentChecklistProps {
  documents: HubDocument[]
  statuses: Record<string, DocumentStatus>
  hubColor: 'green' | 'purple' | 'orange' | 'blue' | 'cyan'
  compact?: boolean
  onAction?: (docName: string, action: HubDocument['actionType']) => void
  onView?: (docName: string) => void
  onReplace?: (docName: string) => void
  readOnly?: boolean
}

const COLOR_MAP = {
  green: {
    badge: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    icon: 'text-green-600 dark:text-green-400',
    border: 'border-green-200 dark:border-green-800',
    bg: 'bg-green-50 dark:bg-green-900/10',
  },
  purple: {
    badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    icon: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800',
    bg: 'bg-purple-50 dark:bg-purple-900/10',
  },
  orange: {
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    icon: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800',
    bg: 'bg-orange-50 dark:bg-orange-900/10',
  },
  blue: {
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    icon: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    bg: 'bg-blue-50 dark:bg-blue-900/10',
  },
  cyan: {
    badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    icon: 'text-cyan-600 dark:text-cyan-400',
    border: 'border-cyan-200 dark:border-cyan-800',
    bg: 'bg-cyan-50 dark:bg-cyan-900/10',
  },
}

function getDocStatus(doc: HubDocument, status?: DocumentStatus): {
  label: string
  completed: boolean
  icon: React.ReactNode
} {
  if (doc.actionType === 'auto-generate') {
    return {
      label: 'Auto-generated',
      completed: true,
      icon: <Sparkles className="h-3.5 w-3.5 text-amber-500" />,
    }
  }
  if (status?.signed) {
    return {
      label: 'Signed',
      completed: true,
      icon: <CheckCircle className="h-3.5 w-3.5 text-green-500" />,
    }
  }
  if (status?.uploaded) {
    return {
      label: 'Uploaded',
      completed: true,
      icon: <CheckCircle className="h-3.5 w-3.5 text-green-500" />,
    }
  }
  return {
    label: doc.required ? 'Required' : 'Optional',
    completed: false,
    icon: doc.required
      ? <AlertCircle className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
      : <Circle className="h-3.5 w-3.5 text-slate-300 dark:text-slate-600" />,
  }
}

function getActionIcon(actionType: HubDocument['actionType']) {
  switch (actionType) {
    case 'upload': return <Upload className="h-3 w-3" />
    case 'e-sign': return <FileSignature className="h-3 w-3" />
    case 'auto-generate': return <Sparkles className="h-3 w-3" />
    case 'review': return <Eye className="h-3 w-3" />
  }
}

function getActionLabel(actionType: HubDocument['actionType']) {
  switch (actionType) {
    case 'upload': return 'Upload'
    case 'e-sign': return 'Sign'
    case 'auto-generate': return 'View'
    case 'review': return 'Review'
  }
}

export const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  documents,
  statuses,
  hubColor,
  compact = false,
  onAction,
  onView,
  onReplace,
  readOnly = false,
}) => {
  const colors = COLOR_MAP[hubColor]
  const completedCount = documents.filter(doc => {
    const s = getDocStatus(doc, statuses[doc.name])
    return s.completed
  }).length

  return (
    <div className={cn('rounded-lg border', colors.border, colors.bg, compact ? 'p-3' : 'p-4')}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className={cn('h-4 w-4', colors.icon)} />
          <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-300">
            Exchange Documents
          </h4>
        </div>
        <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full', colors.badge)}>
          {completedCount}/{documents.length}
        </span>
      </div>

      <div className="space-y-1.5">
        {documents.map((doc) => {
          const status = getDocStatus(doc, statuses[doc.name])
          return (
            <div
              key={doc.name}
              className={cn(
                'flex items-center justify-between py-1.5 px-2.5 rounded-md transition-colors',
                status.completed
                  ? 'bg-white/60 dark:bg-slate-800/40'
                  : 'bg-white/80 dark:bg-slate-800/60'
              )}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {status.icon}
                <div className="min-w-0 flex-1">
                  <p className={cn(
                    'text-xs font-medium truncate',
                    status.completed
                      ? 'text-slate-500 dark:text-slate-400 line-through'
                      : 'text-slate-700 dark:text-slate-200'
                  )}>
                    {doc.name}
                  </p>
                  {!compact && (
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">
                      {doc.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <span className={cn(
                  'text-[10px] font-medium px-1.5 py-0.5 rounded',
                  status.completed
                    ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : doc.required
                    ? 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                    : 'bg-slate-50 text-slate-400 dark:bg-slate-800 dark:text-slate-500'
                )}>
                  {status.label}
                </span>
                {status.completed && onView && (
                  <button
                    type="button"
                    onClick={() => onView(doc.name)}
                    aria-label={`View ${doc.name}`}
                    className={cn(
                      'flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md transition-colors',
                      'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600',
                      'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'
                    )}
                  >
                    <Eye className="h-3 w-3" />
                    View
                  </button>
                )}
                {status.completed && !readOnly && onReplace && doc.actionType !== 'auto-generate' && (
                  <button
                    type="button"
                    onClick={() => onReplace(doc.name)}
                    aria-label={`Replace ${doc.name}`}
                    className={cn(
                      'flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md transition-colors',
                      'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600',
                      'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'
                    )}
                  >
                    <RefreshCw className="h-3 w-3" />
                    Replace
                  </button>
                )}
                {!status.completed && doc.actionType !== 'auto-generate' && onAction && (
                  <button
                    type="button"
                    onClick={() => onAction(doc.name, doc.actionType)}
                    className={cn(
                      'flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-md transition-colors',
                      'bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600',
                      'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600'
                    )}
                  >
                    {getActionIcon(doc.actionType)}
                    {getActionLabel(doc.actionType)}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
